import { lazy, Suspense, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronRight,
  CircleHelp,
  Gift,
  History,
  Image as ImageIcon,
  Inbox,
  KeyRound,
  Landmark,
  LogOut,
  MessagesSquare,
  Pencil,
  Crown,
  Star,
  Trash2,
  WalletCards,
  WalletMinimal,
} from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getBoideToken, logoutBoide } from "@/lib/boide-api";
import { loadAffiliateMe } from "@/lib/bootstrap";
import { useQuotaStatus } from "@/hooks/use-quota-status";
import { planLabelOf } from "@/components/subscription/quota-row";
import { signOutFirebase } from "@/lib/firebase";
import { clearBoideUser, displayName, initials, useBoideUser } from "@/hooks/use-boide-user";
import { trackEvent } from "@/lib/analytics";

const BoideSettingsDialog = lazy(() =>
  import("@/components/boide-settings-dialog").then((module) => ({
    default: module.BoideSettingsDialog,
  })),
);
const ChangePasswordDialog = lazy(() =>
  import("@/components/auth/change-password-dialog").then((module) => ({
    default: module.ChangePasswordDialog,
  })),
);
const DeleteAccountDialog = lazy(() =>
  import("@/components/delete-account-dialog").then((module) => ({
    default: module.DeleteAccountDialog,
  })),
);

const otherItems = [
  { label: "Hỗ trợ", to: "/ho-tro", icon: MessagesSquare, note: "Nhắn cho đội ngũ Bói Đê" },
  {
    label: "Biến động số dư",
    to: "/bien-dong-so-du",
    icon: WalletMinimal,
    note: "Lịch sử thu chi",
    requiresAuth: true,
  },
  { label: "Câu hỏi thường gặp", to: "/faq", icon: CircleHelp, note: "Giải đáp nhanh" },
  {
    label: "Lịch sử xem",
    to: "/lich-su",
    icon: History,
    note: "Quẻ và lá số đã xem",
    requiresAuth: true,
  },
  {
    label: "Ảnh nền của tôi",
    to: "/anh-nen",
    icon: ImageIcon,
    note: "Ảnh nền đã tạo từ quẻ",
    requiresAuth: true,
  },
  {
    label: "Hướng dẫn sử dụng",
    to: "/user-manual",
    icon: BookOpen,
    note: "Cách dùng từng tính năng",
  },
] as const;

const accountItems = [
  {
    label: "Hộp thư",
    to: "/hop-thu",
    icon: Inbox,
    note: "Thư hệ thống & quà tặng",
    requiresAuth: true,
  },
  {
    label: "Gói quota ngày",
    to: "/goi",
    icon: Crown,
    note: "Hạn mức tiêu mỗi ngày, rẻ hơn nạp lẻ",
    requiresAuth: true,
  },
  {
    label: "Giới thiệu & hoa hồng",
    to: "/gioi-thieu",
    icon: Gift,
    note: "Mời bạn bè, quản lý hoa hồng",
    requiresAuth: true,
  },
  {
    label: "Chia sẻ cảm nhận",
    to: "/cam-nhan",
    icon: Star,
    note: "Chia sẻ trải nghiệm của bạn",
    requiresAuth: true,
  },
] as const;

function formatMoney(amount?: number | null) {
  return `${Math.max(0, Number(amount) || 0).toLocaleString("vi-VN")}đ`;
}

export function HomeAccountSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { user } = useBoideUser();
  const authenticated = typeof window !== "undefined" && !!getBoideToken();
  const name = displayName(user, "bạn");

  const affiliate = useQuery({
    queryKey: ["affiliate-me"],
    queryFn: loadAffiliateMe,
    enabled: authenticated && open,
    staleTime: 5 * 60 * 1000,
  });
  // Quota gói — hiện ngay dưới dòng số dư: hai thứ là MỘT túi tiền khi đi tiêu.
  const quota = useQuotaStatus({ enabled: authenticated && open });
  const quotaPlan = planLabelOf(quota.data?.subscriptions ?? []);

  const handleLogout = async () => {
    trackEvent("logout", { source: "home_account_sheet" });
    logoutBoide();
    clearBoideUser();
    await signOutFirebase();
    toast.success("Đã thoát khỏi cõi giới.");
    onOpenChange(false);
    navigate({ to: "/login" });
  };

  const openProfile = () => {
    onOpenChange(false);
    setProfileOpen(true);
  };

  const openDeleteAccount = () => {
    onOpenChange(false);
    setDeleteOpen(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[86svh] overflow-y-auto rounded-t-3xl border-border/70 px-0 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="px-5 text-left">
            <SheetTitle className="font-display text-base">Tài khoản</SheetTitle>
            <SheetDescription className="sr-only">Xem số dư và quản lý tài khoản.</SheetDescription>
          </SheetHeader>

          <div className="mt-3 px-5">
            {authenticated ? (
              <div className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card p-3">
                <Avatar className="size-11 border border-primary/25">
                  {user?.avatar ? <AvatarImage src={user.avatar} alt={name} /> : null}
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {initials(name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{name}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <WalletCards className="size-3" />
                      Số dư{" "}
                      <strong className="font-semibold tabular-nums text-primary">
                        {formatMoney(user?.amount)}
                      </strong>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Landmark className="size-3" />
                      Hoa hồng{" "}
                      <strong className="font-semibold tabular-nums text-foreground">
                        {formatMoney(affiliate.data?.commission_balance)}
                      </strong>
                    </span>
                  </p>
                  {quota.data ? (
                    <p className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                      <Crown className="size-3 shrink-0 text-primary" />
                      {quotaPlan.primary ? (
                        <span className="truncate">
                          {/* Tên gói đã chứa chữ "Gói" — xem ghi chú ở `QuotaRow`. */}
                          <strong className="font-semibold text-foreground">
                            {quotaPlan.primary.plan_name}
                          </strong>
                          {quotaPlan.extra > 0 ? (
                            <span className="text-primary"> +{quotaPlan.extra}</span>
                          ) : null}{" "}
                          · còn{" "}
                          <strong className="font-semibold tabular-nums text-primary">
                            {formatMoney(quota.data.remaining)}
                          </strong>{" "}
                          hôm nay
                        </span>
                      ) : (
                        <span className="truncate">Chưa có gói quota ngày</span>
                      )}
                    </p>
                  ) : null}
                </div>
                <Link
                  to="/thanh-toan"
                  onClick={() => onOpenChange(false)}
                  className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"
                >
                  Nạp tiền
                </Link>
              </div>
            ) : (
              <div className="rounded-2xl border border-border/70 bg-card p-4">
                <p className="text-sm font-semibold text-foreground">
                  Đăng nhập để lưu lá số và quản lý số dư
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Tài khoản mới được tặng 5.000đ xem bói.
                </p>
                <Link
                  to="/login"
                  search={{ returnTo: "/" }}
                  onClick={() => onOpenChange(false)}
                  className="mt-3 inline-flex rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"
                >
                  Đăng nhập
                </Link>
              </div>
            )}
          </div>

          <SheetGroup title="Tài khoản">
            {authenticated ? (
              <>
                <SheetActionRow
                  label="Chỉnh sửa hồ sơ"
                  note="Thông tin cá nhân và ảnh đại diện"
                  icon={Pencil}
                  onClick={openProfile}
                />
                <SheetActionRow
                  label="Mật khẩu đăng nhập"
                  note="Đổi hoặc thêm mật khẩu cho tài khoản"
                  icon={KeyRound}
                  onClick={() => setPasswordOpen(true)}
                />
                <SheetActionRow
                  label="Xóa tài khoản"
                  note="Xóa vĩnh viễn tài khoản và dữ liệu"
                  icon={Trash2}
                  onClick={openDeleteAccount}
                  destructive
                />
              </>
            ) : null}
            {accountItems.map((item) => (
              <SheetRow
                key={item.label}
                item={item}
                authenticated={authenticated}
                onNavigate={() => onOpenChange(false)}
              />
            ))}
          </SheetGroup>

          <SheetGroup title="Mục khác">
            {otherItems.map((item) => (
              <SheetRow
                key={item.label}
                item={item}
                authenticated={authenticated}
                onNavigate={() => onOpenChange(false)}
              />
            ))}
          </SheetGroup>

          {authenticated ? (
            <div className="mt-4 px-5">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-destructive/30 py-2.5 text-sm font-semibold text-destructive transition hover:bg-destructive/10"
              >
                <LogOut className="size-4" />
                Đăng xuất
              </button>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {authenticated && (profileOpen || deleteOpen || passwordOpen) ? (
        <Suspense fallback={null}>
          {profileOpen ? (
            <BoideSettingsDialog open={profileOpen} onOpenChange={setProfileOpen} />
          ) : null}
          {passwordOpen ? (
            <ChangePasswordDialog
              open={passwordOpen}
              onOpenChange={setPasswordOpen}
              onChanged={() => onOpenChange(false)}
            />
          ) : null}
          {deleteOpen ? (
            <DeleteAccountDialog
              open={deleteOpen}
              onOpenChange={setDeleteOpen}
              onDeleted={() => onOpenChange(false)}
            />
          ) : null}
        </Suspense>
      ) : null}
    </>
  );
}

function SheetGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4">
      <h3 className="px-5 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </h3>
      <div className="mt-2 divide-y divide-border/60 border-y border-border/60">{children}</div>
    </section>
  );
}

function SheetRow({
  item,
  authenticated,
  onNavigate,
}: {
  item: (typeof otherItems)[number] | (typeof accountItems)[number];
  authenticated: boolean;
  onNavigate: () => void;
}) {
  const requiresLogin = "requiresAuth" in item && item.requiresAuth && !authenticated;

  if (requiresLogin) {
    return (
      <Link
        to="/login"
        search={{ returnTo: item.to }}
        onClick={onNavigate}
        className="flex items-center gap-3 px-5 py-2.5 transition hover:bg-primary/5"
      >
        <SheetRowContent item={item} />
      </Link>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className="flex items-center gap-3 px-5 py-2.5 transition hover:bg-primary/5"
    >
      <SheetRowContent item={item} />
    </Link>
  );
}

function SheetActionRow({
  label,
  note,
  icon: Icon,
  onClick,
  destructive = false,
}: {
  label: string;
  note: string;
  icon: typeof Pencil;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-5 py-2.5 text-left transition hover:bg-primary/5"
    >
      <span
        className={
          destructive
            ? "flex size-8 shrink-0 items-center justify-center text-destructive"
            : "flex size-8 shrink-0 items-center justify-center text-primary"
        }
      >
        <Icon className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <strong
          className={
            destructive
              ? "block truncate text-sm font-semibold text-destructive"
              : "block truncate text-sm font-semibold text-foreground"
          }
        >
          {label}
        </strong>
        <span className="block truncate text-[11px] text-muted-foreground">{note}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

function SheetRowContent({
  item,
}: {
  item: (typeof otherItems)[number] | (typeof accountItems)[number];
}) {
  return (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center text-primary">
        <item.icon className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-sm font-semibold text-foreground">
          {item.label}
        </strong>
        <span className="block truncate text-[11px] text-muted-foreground">{item.note}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </>
  );
}
